import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import VipRestriction from '../components/VipRestriction';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import {
  Card,
  Button,
  Typography,
  Row,
  Col,
  Space,
  Tag,
  Form,
  Input,
  Select,
  Popconfirm,
  message,
  Statistic,
  Steps,
  Timeline,
  Divider,
  Tabs,
  DatePicker
} from 'antd';
import {
  BulbOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowLeftOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  TrophyOutlined,
  StarOutlined,
  FireOutlined,
  ClockCircleOutlined as ClockIcon,
  CheckCircleOutlined as CheckIcon,
  ExclamationCircleOutlined,
  CalendarOutlined,
  UserOutlined,
  SettingOutlined,
  InfoCircleOutlined,
  EyeOutlined,
  HistoryOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  MessageOutlined,
  HomeOutlined,
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined as CalendarIcon,
  FileTextOutlined as FileIcon,
  HeartOutlined as HeartIcon,
  MessageOutlined as MessageIcon,
  BulbOutlined as BulbIcon
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { creativeConfig } from '../config/creative';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { Step } = Steps;

const Creative = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [ideas, setIdeas] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showIdeaModal, setShowIdeaModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showTipsModal, setShowTipsModal] = useState(false);
  const [selectedIdea, setSelectedIdea] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [ideaForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 获取创意列表
  const fetchIdeas = async () => {
    setLoading(true);
    try {
      const response = await axios.get(creativeConfig.apiEndpoints.ideas);
      if (response.data.success) {
        setIdeas(response.data.ideas || []);
      } else {
        message.error(response.data.message || '获取创意列表失败');
      }
    } catch (error) {
      console.error('获取创意列表失败:', error);
      message.error('获取创意列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取创意类型
  const fetchTypes = async () => {
    try {
      const response = await axios.get(creativeConfig.apiEndpoints.types);
      if (response.data.success) {
        setTypes(response.data.types || []);
      }
    } catch (error) {
      console.error('获取创意类型失败:', error);
    }
  };

  // 打开创意弹窗
  const handleOpenIdea = (idea = null) => {
    setSelectedIdea(idea);
    if (idea) {
      ideaForm.setFieldsValue({
        ...idea,
        created_date: idea.created_date ? dayjs(idea.created_date) : null,
        target_date: idea.target_date ? dayjs(idea.target_date) : null
      });
    } else {
      ideaForm.resetFields();
    }
    setShowIdeaModal(true);
  };

  // 打开类型弹窗
  const handleOpenType = (type = null) => {
    setSelectedType(type);
    if (type) {
      typeForm.setFieldsValue(type);
    } else {
      typeForm.resetFields();
    }
    setShowTypeModal(true);
  };

  // 保存创意
  const handleSaveIdea = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values,
        created_date: values.created_date ? values.created_date.format('YYYY-MM-DD') : null,
        target_date: values.target_date ? values.target_date.format('YYYY-MM-DD') : null
      };

      let response;
      if (selectedIdea) {
        response = await axios.put(`${creativeConfig.apiEndpoints.updateIdea}/${selectedIdea.id}`, requestBody);
      } else {
        response = await axios.post(creativeConfig.apiEndpoints.createIdea, requestBody);
      }

      if (response.data.success) {
        message.success(selectedIdea ? '更新成功' : '创建成功');
        setShowIdeaModal(false);
        fetchIdeas();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存创意失败:', error);
      message.error('操作失败');
    }
  };

  // 保存类型
  const handleSaveType = async (values) => {
    try {
      const requestBody = {
        // user_id 现在从JWT token中获取，不需要在请求中传递
        ...values
      };

      let response;
      if (selectedType) {
        response = await axios.put(`${creativeConfig.apiEndpoints.updateType}/${selectedType.id}`, requestBody);
      } else {
        response = await axios.post(creativeConfig.apiEndpoints.createType, requestBody);
      }

      if (response.data.success) {
        message.success(selectedType ? '更新成功' : '创建成功');
        setShowTypeModal(false);
        fetchTypes();
      } else {
        message.error(response.data.message || '操作失败');
      }
    } catch (error) {
      console.error('保存类型失败:', error);
      message.error('操作失败');
    }
  };

  // 删除创意
  const handleDeleteIdea = async (id) => {
    try {
      const response = await axios.delete(`${creativeConfig.apiEndpoints.deleteIdea}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchIdeas();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除创意失败:', error);
      message.error('删除失败');
    }
  };

  // 删除类型
  const handleDeleteType = async (id) => {
    try {
      const response = await axios.delete(`${creativeConfig.apiEndpoints.deleteType}/${id}`);
      if (response.data.success) {
        message.success('删除成功');
        fetchTypes();
      } else {
        message.error(response.data.message || '删除失败');
      }
    } catch (error) {
      console.error('删除类型失败:', error);
      message.error('删除失败');
    }
  };

  // 获取类型颜色
  const getTypeColor = (typeKey) => {
    const type = creativeConfig.ideaTypes.find(t => t.key === typeKey);
    return type ? type.color : '#D9D9D9';
  };

  // 获取类型名称
  const getTypeName = (typeKey) => {
    const type = creativeConfig.ideaTypes.find(t => t.key === typeKey);
    return type ? type.name : '未知';
  };

  // 获取类型图标
  const getTypeIcon = (typeKey) => {
    const type = creativeConfig.ideaTypes.find(t => t.key === typeKey);
    return type ? type.icon : '📝';
  };

  // 获取来源名称
  const getSourceName = (sourceKey) => {
    const source = creativeConfig.ideaSources.find(s => s.key === sourceKey);
    return source ? source.name : '未知';
  };

  // 获取来源图标
  const getSourceIcon = (sourceKey) => {
    const source = creativeConfig.ideaSources.find(s => s.key === sourceKey);
    return source ? source.icon : '📝';
  };

  // 获取实施阶段名称
  const getStageName = (stageKey) => {
    const stage = creativeConfig.implementationStages.find(s => s.key === stageKey);
    return stage ? stage.name : '未知';
  };

  // 获取实施阶段颜色
  const getStageColor = (stageKey) => {
    switch (stageKey) {
      case 'concept':
        return 'blue';
      case 'prototype':
        return 'orange';
      case 'testing':
        return 'purple';
      case 'pilot':
        return 'green';
      case 'full_launch':
        return 'red';
      case 'completed':
        return 'success';
      default:
        return 'default';
    }
  };

  // 计算统计信息
  const totalIdeas = ideas.length;
  const implementedIdeas = ideas.filter(idea => idea.implementation_stage === 'completed').length;
  const inProgressIdeas = ideas.filter(idea => idea.implementation_stage !== 'concept' && idea.implementation_stage !== 'completed').length;
  const implementationRate = totalIdeas > 0 ? Math.round((implementedIdeas / totalIdeas) * 100) : 0;

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchIdeas();
    fetchTypes();
  }, [initialize]);

  return (
    <VipRestriction>
      <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/')}
            >
              返回
            </Button>
            <h1 className="mod-header-title">
              <BulbOutlined style={{ marginRight: '8px', color: '#6366F1' }} />
              {creativeConfig.name}
            </h1>
          </div>
          <div className="mod-header-subtitle">
            记录、评估与实施你的创意想法，从灵感到落地全流程管理
          </div>
        </div>

        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => handleOpenIdea()}
            style={{ backgroundColor: '#6366F1', borderColor: '#6366F1' }}
          >
            新增创意
          </Button>
          <Button
            icon={<SettingOutlined />}
            onClick={() => handleOpenType()}
          >
            创意类型
          </Button>
          <Button
            icon={<InfoCircleOutlined />}
            onClick={() => setShowTipsModal(true)}
          >
            创意TIPS
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon">💡</div>
          <div>
            <div className="mod-stat-value">{totalIdeas}</div>
            <div className="mod-stat-label">创意总数</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon">🚀</div>
          <div>
            <div className="mod-stat-value">{inProgressIdeas}</div>
            <div className="mod-stat-label">实施中</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon">✅</div>
          <div>
            <div className="mod-stat-value">{implementedIdeas}</div>
            <div className="mod-stat-label">已完成</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon">📊</div>
          <div>
            <div className="mod-stat-value">{implementationRate}%</div>
            <div className="mod-stat-label">实施完成率</div>
          </div>
        </div>
      </div>

      {/* 主要内容 */}
      <div>
        <Row gutter={[16, 16]}>
        {ideas.map((idea) => (
          <Col xs={24} sm={12} md={8} lg={6} key={idea.id}>
            <Card
              hoverable
              style={{ 
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                border: 'none',
                transition: 'all 0.3s ease'
              }}
              styles={{ body: { padding: '6px' } }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
              }}
              actions={[
                <EditOutlined key="edit" onClick={() => handleOpenIdea(idea)} />,
                <Popconfirm
                  title="确定删除这个创意吗？"
                  onConfirm={() => handleDeleteIdea(idea.id)}
                  okText="确定"
                  cancelText="取消"
                >
                  <DeleteOutlined key="delete" />
                </Popconfirm>
              ]}
            >
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', marginBottom: '8px' }}>
                  {getTypeIcon(idea.idea_type)}
                </div>
                <Title level={5} style={{ margin: '8px 0' }}>
                  {idea.title}
                </Title>
                <Tag color={getTypeColor(idea.idea_type)} style={{ marginBottom: '8px' }}>
                  {getTypeName(idea.idea_type)}
                </Tag>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    {getSourceIcon(idea.source)} {getSourceName(idea.source)}
                  </Text>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    📅 {idea.created_date ? dayjs(idea.created_date).format('MM-DD') : '未设置'}
                  </Text>
                </div>
                <div style={{ marginTop: '8px' }}>
                  <Tag color={getStageColor(idea.implementation_stage)}>
                    {getStageName(idea.implementation_stage)}
                  </Tag>
                </div>
                {idea.description && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                    {idea.description}
                  </Text>
                )}
                {idea.evaluation && (
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '4px' }}>
                    📊 {idea.evaluation}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
        {ideas.length === 0 && (
          <Col span={24}>
            <Card>
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <BulbOutlined style={{ fontSize: '48px', color: '#D9D9D9', marginBottom: '16px' }} />
                <Title level={4} style={{ color: '#D9D9D9' }}>暂无创意</Title>
                <Text type="secondary">点击"新增创意"开始记录你的创意想法</Text>
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* 创意弹窗 */}
      {showIdeaModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {selectedIdea ? '编辑' : '新增'}创意
              </h3>
              <Button
                type="text"
                onClick={() => setShowIdeaModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={ideaForm}
          layout="vertical"
          onFinish={handleSaveIdea}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="idea_type"
                label="创意类型"
                rules={[{ required: true, message: '请选择创意类型' }]}
              >
                <Select placeholder="请选择类型">
                  {creativeConfig.ideaTypes.map(type => (
                    <Option key={type.key} value={type.key}>
                      <span style={{ marginRight: '8px' }}>{type.icon}</span>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="source"
                label="创意来源"
                rules={[{ required: true, message: '请选择创意来源' }]}
              >
                <Select placeholder="请选择来源">
                  {creativeConfig.ideaSources.map(source => (
                    <Option key={source.key} value={source.key}>
                      <span style={{ marginRight: '8px' }}>{source.icon}</span>
                      {source.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="title"
            label="创意标题"
            rules={[{ required: true, message: '请输入创意标题' }]}
          >
            <Input placeholder="请输入创意标题" />
          </Form.Item>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="created_date"
                label="创建日期"
              >
                <DatePicker style={{ width: '100%' }} placeholder="创意产生日期" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="target_date"
                label="目标日期"
              >
                <DatePicker style={{ width: '100%' }} placeholder="预期完成日期" />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            name="description"
            label="创意描述"
          >
            <TextArea rows={3} placeholder="请详细描述你的创意想法" />
          </Form.Item>
          
          <Form.Item
            name="evaluation"
            label="评估结果"
          >
            <TextArea rows={2} placeholder="请记录创意评估结果" />
          </Form.Item>
          
          <Form.Item
            name="implementation_stage"
            label="实施阶段"
            rules={[{ required: true, message: '请选择实施阶段' }]}
          >
            <Select placeholder="请选择阶段">
              {creativeConfig.implementationStages.map(stage => (
                <Option key={stage.key} value={stage.key}>
                  <span style={{ marginRight: '8px' }}>{stage.icon}</span>
                  {stage.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item
            name="feedback"
            label="反馈意见"
          >
            <TextArea rows={2} placeholder="请记录反馈意见" />
          </Form.Item>
          
          <Form.Item
            name="incentive"
            label="激励措施"
          >
            <TextArea rows={2} placeholder="请记录激励措施" />
          </Form.Item>
          
          <Form.Item
            name="notes"
            label="备注"
          >
            <TextArea rows={2} placeholder="请输入备注信息" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowIdeaModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => ideaForm.submit()}>
                {selectedIdea ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 类型管理弹窗 */}
      {showTypeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '500px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                {selectedType ? '编辑' : '新增'}创意类型
              </h3>
              <Button
                type="text"
                onClick={() => setShowTypeModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={handleSaveType}
        >
          <Form.Item
            name="name"
            label="类型名称"
            rules={[{ required: true, message: '请输入类型名称' }]}
          >
            <Input placeholder="请输入类型名称" />
          </Form.Item>
          
          <Form.Item
            name="description"
            label="类型描述"
          >
            <TextArea rows={3} placeholder="请输入类型描述" />
          </Form.Item>
          
          <Form.Item
            name="color"
            label="显示颜色"
          >
            <Input placeholder="请输入颜色代码，如：#1890FF" />
          </Form.Item>
          
        </Form>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '8px',
              marginTop: '20px',
              paddingTop: '12px',
              borderTop: '1px solid #f0f0f0'
            }}>
              <Button onClick={() => setShowTypeModal(false)}>
                取消
              </Button>
              <Button type="primary" onClick={() => typeForm.submit()}>
                {selectedType ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 创意TIPS弹窗 */}
      {showTipsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '24px',
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              paddingBottom: '12px',
              borderBottom: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                <BulbIcon style={{ color: creativeConfig.bgColor, marginRight: '8px' }} />
                创意管理TIPS
              </h3>
              <Button
                type="text"
                onClick={() => setShowTipsModal(false)}
                style={{ fontSize: '18px' }}
              >
                ×
              </Button>
            </div>
        <div style={{ padding: '16px 0' }}>
          {/* 创意管理流程 */}
          <Card title="创意管理五大流程" style={{ marginBottom: '16px' }}>
            <Steps direction="vertical" size="small">
              {creativeConfig.creativeManagementTips.process.map((step, index) => (
                <Step
                  key={index}
                  title={
                    <div>
                      <span style={{ marginRight: '8px' }}>{step.icon}</span>
                      {step.title}
                    </div>
                  }
                  description={
                    <div>
                      <Text strong>{step.description}</Text>
                      <br />
                      <Text type="secondary">{step.details}</Text>
                    </div>
                  }
                />
              ))}
            </Steps>
          </Card>

          {/* 创意管理原则 */}
          <Card title="创意管理核心原则" style={{ marginBottom: '16px' }}>
            <Timeline>
              {creativeConfig.creativePrinciples.principles.map((principle, index) => (
                <Timeline.Item key={index}>
                  <Title level={5}>
                    <span style={{ marginRight: '8px' }}>{principle.icon}</span>
                    {principle.title}
                  </Title>
                  <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                    {principle.description}
                  </Text>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>

          {/* 功能说明 */}
          <Card title="功能模块说明">
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>💡 创意管理</Title>
                  <Text type="secondary">记录和管理创意想法，从产生到实施的完整流程</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>📝 创意收集</Title>
                  <Text type="secondary">收集和整理各种来源的创意想法</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🔍 创意评估</Title>
                  <Text type="secondary">对创意进行可行性、价值、风险等评估</Text>
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🚀 创意实施</Title>
                  <Text type="secondary">跟踪创意的实施进度和阶段</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>💬 反馈收集</Title>
                  <Text type="secondary">收集实施过程中的反馈意见</Text>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <Title level={5}>🎁 激励管理</Title>
                  <Text type="secondary">记录对创意贡献者的激励措施</Text>
                </div>
              </Col>
            </Row>
          </Card>
        </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </VipRestriction>
  );
};

export default Creative;
